import { modulesVerrouillesSchema, type AcceptationCgu, type LangueCgu } from '@artosera/shared';
import { ErreurReponseIllisible } from './errors.js';
import { api } from './client.js';
import type {
  ReponseConfirmationMfa,
  ReponseConnexion,
  ReponseEnrolementMfa,
  ReponseInvitation,
  ReponseMessage,
  ReponseMoi,
  SessionUtilisateur,
} from './types.js';

export interface IdentifiantsConnexion {
  readonly email: string;
  readonly motDePasse: string;
  readonly codeTotp?: string;
  readonly codeSecours?: string;
}

export const authApi = {
  ssoDeuxFacteurs: (corps: { code?: string; codeSecours?: string }) =>
    api.post<{ retour: string }>('/auth/sso/2fa', corps),
  moi: async () => {
    const reponse = await api.get<ReponseMoi>('/auth/me');
    if (!reponse?.compte?.id || !Array.isArray(reponse.galeries))
      throw new ErreurReponseIllisible();
    return normaliserSession(reponse);
  },

  connexion: (identifiants: IdentifiantsConnexion) =>
    api.post<ReponseConnexion>('/auth/login', identifiants),

  deconnexion: () => api.post<void>('/auth/logout'),

  motDePasseOublie: (email: string) => api.post<ReponseMessage>('/auth/password/forgot', { email }),

  motDePasseReinitialiser: (jeton: string, motDePasse: string) =>
    api.post<ReponseMessage>('/auth/password/reset', { jeton, motDePasse }),

  // Le jeton voyage dans le CORPS, jamais dans le chemin : l'API journalise
  // `req.url`, et un jeton d'invitation dans un journal se rejoue sept jours
  // (`POST /v1/invitations/accept`, docs/dev/api.md § 3).
  invitationAccepter: (jeton: string, corps: { nom?: string; motDePasse: string }) =>
    api.post<ReponseInvitation>('/invitations/accept', { jeton, ...corps }),

  mfaEnrolement: () => api.post<ReponseEnrolementMfa>('/auth/2fa/enroll'),

  mfaConfirmer: (code: string) => api.post<ReponseConfirmationMfa>('/auth/2fa/confirm', { code }),

  /**
   * Élévation (step-up) d'une session déjà ouverte, avant un geste sensible
   * (`docs/dev/api.md` § 3 : coffre confidentiel, export complet, jeton,
   * accès de support) — `apps/api/src/mfa/mfa.routes.ts`, `POST …/2fa/verify`.
   * Fenêtre de quinze minutes ; un compte sans second facteur actif ni rôle
   * privilégié n'a jamais besoin d'appeler cette route (`exigerMfaRecente`
   * laisse alors passer sans poser la question).
   */
  mfaVerifier: (corps: { code?: string; codeSecours?: string }) =>
    api.post<{ verifie: boolean }>('/auth/2fa/verify', corps),

  mfaDesactiver: (corps: { code?: string; codeSecours?: string }) =>
    api.post<ReponseMessage>('/auth/2fa/disable', corps),

  /**
   * CGU (ARTO-59) : accepter la version AFFICHÉE, dans la langue affichée au
   * moment du geste. `409 cgu_version_perimee` : ce n'est plus la version en
   * vigueur.
   */
  cguAccepter: (corps: { version: string; langue: LangueCgu }) =>
    api.post<{ cgu: AcceptationCgu }>('/auth/cgu/accepter', corps),

  /** Refuser les CGU : le refus est signalé à la galerie, la session se ferme. */
  cguRefuser: () => api.post<void>('/auth/cgu/refuser'),
};

/**
 * Les champs de `/me` hors compte et galeries, gardés par la normalisation : les
 * CGU et l'exigence de double authentification tels quels, les modules
 * verrouillés validés (une valeur illisible n'annonce rien plutôt que de
 * casser la session).
 */
function champsSession(
  brute: ReponseMoi,
): Pick<
  SessionUtilisateur,
  | 'cguAcceptationRequise'
  | 'cgu'
  | 'modulesVerrouilles'
  | 'enrolementMfaRequis'
  | 'enrolementMfaSeulement'
> {
  const verrouilles = modulesVerrouillesSchema.safeParse(brute.modulesVerrouilles);
  return {
    ...(verrouilles.success ? { modulesVerrouilles: verrouilles.data } : {}),
    ...(brute.cguAcceptationRequise ? { cguAcceptationRequise: brute.cguAcceptationRequise } : {}),
    ...(brute.cgu === undefined ? {} : { cgu: brute.cgu }),
    ...(brute.enrolementMfaRequis === true ? { enrolementMfaRequis: true } : {}),
    ...(brute.enrolementMfaSeulement === true ? { enrolementMfaSeulement: true } : {}),
  };
}

export function normaliserSession(brute: ReponseMoi): SessionUtilisateur {
  if (!brute.groupes || brute.groupes.length === 0) {
    return { compte: brute.compte, galeries: brute.galeries, ...champsSession(brute) };
  }
  const idParSlug = new Map(
    brute.galeries.flatMap((galerie) =>
      galerie.slug ? [[galerie.slug, galerie.id] as const] : [],
    ),
  );
  return {
    galeries: brute.galeries,
    ...champsSession(brute),
    compte: {
      ...brute.compte,
      groupes: brute.groupes.map((groupe) => ({
        id: groupe.id,
        slug: groupe.slug,
        nom: groupe.nom ?? '',
        role: groupe.role,
        galeries: groupe.galeries.flatMap((slug) => {
          const id = idParSlug.get(slug);
          return id === undefined ? [] : [id];
        }),
      })),
    },
  };
}
